import { DateTime, LocaleOptions } from "luxon";

// Given an ISO formatted date string, parses that string into a
// local Date object. Interprets the dateString as a UTC string
export const parseUTCDateFromString = (dateString: string): Date => {
  let utcDateString = dateString;
  // Matches timestamps without tz information, the regex looks for an
  // offset like +01:00 or -07:00
  if (!dateString.endsWith("Z") && !dateString.match(".*:..*[+-]")) {
    utcDateString = `${utcDateString}Z`;
  }
  utcDateString = utcDateString.replace(" ", "T");

  return new Date(utcDateString);
};

export const optionalParseUTCDateTime = (
  dateToParse?: string | number | DateTime | null,
): DateTime | undefined =>
  dateToParse ? parseUTCDateTime(dateToParse) : undefined;

export const parseUTCDateTime = (
  dateToParse: string | number | DateTime,
): DateTime => {
  if (typeof dateToParse === "number") {
    return DateTime.fromMillis(dateToParse);
  }
  if (typeof dateToParse === "string") {
    const jsDate = parseUTCDateFromString(dateToParse);
    return DateTime.fromJSDate(jsDate);
  }
  return dateToParse;
};

export const parseUTCDateTimeFromString = (dateString: string): DateTime => {
  const date = parseUTCDateFromString(dateString);
  return DateTime.fromJSDate(date);
};

export const optionalParseUTCDateTimeFromString = (
  dateString?: string | null,
): DateTime | undefined => {
  return dateString ? parseUTCDateTimeFromString(dateString) : undefined;
};

export const parseLocalDateTimeFromString = (dateString: string): DateTime => {
  return DateTime.fromISO(dateString);
};

export const parseUTCDateTimeToTimezoneFromString = (
  dateString: string,
  zone?: string,
): DateTime => {
  if (!zone) return parseUTCDateTimeFromString(dateString);

  const date = parseUTCDateFromString(dateString);
  return DateTime.fromJSDate(date, { zone });
};

/** Example format: "10:30-11:00 am" */
export const formatStartAndEndTime = (
  startTime?: DateTime,
  endTime?: DateTime | null,
  opts?: LocaleOptions,
) => {
  if (!startTime && !endTime) return null;

  if (!startTime) {
    return endTime!.toLocaleString({ timeStyle: "short" }, opts);
  }

  if (!endTime) {
    return startTime.toLocaleString({ timeStyle: "short" }, opts);
  }

  return `${startTime.toLocaleString({ timeStyle: "short" }, opts)} - ${endTime.toLocaleString({ timeStyle: "short" }, opts)}`;
};
